import {storesGenerator} from '@optimaxdev/utils';

import type {ACCESSORY_CLICK} from 'constants/accessory';
import type {ALGOLIA_QUERY_MAP} from 'constants/algolia';
import type {
    DATALAYER_EMAIL,
    ONETRUST_COOKIE,
    LOCAL_STORAGE_CHECKOUT,
    LOCAL_STORAGE_ORDER_DATA,
    SESSION_STORAGE_POST_PURCHASE_ORDER_DATA,
    IRE_CLICK_ID,
    PURCHASE_STORAGE,
    IS_OTTICA_DIMENSION_EVENT,
    PAYPAL_BUTTON_POSITION,
} from 'constants/analytics';
import type {SEGMENT_FOR_MENTION_ME} from 'constants/analytics/mentionMe';
import type {CATEGORY_LIST} from 'constants/analytics/storages';
import type {GENDER_FROM_POPUP} from 'constants/category';
import type {EXTRA_URL_COOKIE_KEY, ITEMS_WITH_PRESCRIPTION_TYPE_KEY} from 'constants/constants';
import type {
    IS_CL_COUPON_SHOWED_SENT_STORAGE_KEY,
    PAYPAL_PURCHASE_STORAGE,
} from 'constants/session';
import type {UHC_COOKIE} from 'constants/uhc';
import type {ACCESSORY_QUANTITY_MORE_THAN_TWO} from 'desktop/pages/cart/components/accessoryQuantityButton/accessoryQuantityButton';
import type {
    PDP_RECENTLY_VIEWED_PRODUCT_LIST,
    RECENTLY_VIEWED_PRODUCT_LIST,
    RECOMMENDATION_JUMP_PAGE,
    SIMILAR_FRAMES,
    VIEWED_PRODUCT_LIST,
} from 'desktop/pages/cart/features/recommendation/recommendationProductView';
import type {RETURNING_CUSTOMER_COOKIE_KEY} from 'desktop/pages/cart/hooks/returningCustomerCL/constants';

import type {IS_SHIPPING_PROTECTION_TOGGLED} from 'desktop/pages/checkout/components/shippingProtection/toggleShippingProtection/toggleShippingProtection';
import type {
    CURRENT_STEP_KEY,
    LOGIN_METHOD,
    STEPS,
    SUBSCRIBER_BY_PHONE,
} from 'desktop/pages/checkout/constants/checkout';
import type {AFF_ID_STORE_PARAM} from 'libs/analytics/affiliate';
import type {ACCESSORY_SLIDE_IMG} from 'mobile/pages/cart/components/accessoryMedia/accessoryMedia';
import type {SAVED_CHECKOUT_DATA_HASH_KEY} from 'mobile/pages/checkout/components/shipping/hooks/useSilentSaveOnCheckout/useSilentSaveOnCheckout';

export type LocalStoreType = {
    QuickCheckoutInitialized: boolean;
    [ITEMS_WITH_PRESCRIPTION_TYPE_KEY]: {[key: string]: string};
    [AFF_ID_STORE_PARAM]: string;
    [IRE_CLICK_ID]: string;
    [SEGMENT_FOR_MENTION_ME]: string;
    'persist:checkoutPersistentStore': string;
    [CATEGORY_LIST]: {id: number | string; category: string; list: string}[];
    [LOCAL_STORAGE_ORDER_DATA]: string;
    [LOCAL_STORAGE_CHECKOUT]: string;
    [CURRENT_STEP_KEY]: STEPS;
    [IS_SHIPPING_PROTECTION_TOGGLED]: string;
    email: string;
    [LOGIN_METHOD]: string;
    popUpEmail: string;
    subscriptionEmail: string;
    [RECOMMENDATION_JUMP_PAGE]: string;
    cartUpdated: string | number;
    cartAdded: string | number;
    quoteItemsImages: string[];
    [ALGOLIA_QUERY_MAP]: string;
};

type SessionStoreType = {
    [SESSION_STORAGE_POST_PURCHASE_ORDER_DATA]: string;
    [DATALAYER_EMAIL]: string[];
    [PURCHASE_STORAGE]: string;
    [IS_OTTICA_DIMENSION_EVENT]: boolean;
    shouldShowMiniCart: boolean;
    'GeneralGeneralBrowserTabNewTab-Opened': boolean;
    [PAYPAL_PURCHASE_STORAGE]: string;
    [SIMILAR_FRAMES]: string[];
    [RECENTLY_VIEWED_PRODUCT_LIST]: string[];
    [PDP_RECENTLY_VIEWED_PRODUCT_LIST]: string[];
    [VIEWED_PRODUCT_LIST]: string[];
    toPDPFrom: string;
    [ACCESSORY_CLICK.LEARN_MORE]: boolean;
    [ACCESSORY_CLICK.CLOSE_POPUP]: boolean;
    [ACCESSORY_QUANTITY_MORE_THAN_TWO]: boolean;
    [PAYPAL_BUTTON_POSITION]: string;
    [ACCESSORY_SLIDE_IMG]: boolean;
    ctpPlaceOfInteraction: string;
    [SAVED_CHECKOUT_DATA_HASH_KEY]: string;
    [IS_CL_COUPON_SHOWED_SENT_STORAGE_KEY]: boolean;
    isCouponShowedSent: boolean;
};

type CookieStoreType = {
    [AFF_ID_STORE_PARAM]: string;
    [ONETRUST_COOKIE]: string;
    in_network: keyof typeof UHC_COOKIE.VALUES;
    _dyjsession: string;
    _dyid_server: string | number;
    'x-akamai-geo'?: string;
    SUBSCRIBER: string;
    [EXTRA_URL_COOKIE_KEY]: string;
    [SUBSCRIBER_BY_PHONE]: string;
    [RETURNING_CUSTOMER_COOKIE_KEY]: string;
    [GENDER_FROM_POPUP]: string;
    visitorId: string;
    isLuxuryExperience: number;
};

export const localStore = storesGenerator.generateLocalStore<LocalStoreType>();
export const sessionStore = storesGenerator.generateSessionStore<SessionStoreType>();
export const cookieStore = storesGenerator.generateCookieStore<CookieStoreType>();
